<template>
  <div class="page-gutter">
    <!-- "Your store", not "Today": the page is a 30-day read. -->
    <Section :title="t('today.home.title', { company: companyName })">
      <!-- Intro — the one place Allen is introduced (avatar inline after his name). Shown
           on the first few visits only (it costs a row on every return visit), and always
           while Allen is off, because then it carries the "turn him on" line. -->
      <div v-if="showIntro || (!loading && !coachEnabled)" class="home-intro">
        <p class="intro-lead">
          {{ t('today.intro.meet') }} <span class="intro-name">Allen</span><span class="intro-bot" aria-hidden="true"><i class="bi bi-robot"></i></span>{{ t('today.intro.companion') }}
        </p>
        <p v-if="!loading" class="intro-sub">
          <template v-if="coachEnabled">{{ t('today.intro.subOn') }}</template>
          <template v-else>{{ t('today.intro.subOff') }}</template>
        </p>
      </div>

      <!-- One loader until BOTH the home-summary and Today have resolved, so the page
           never renders piecemeal (hero 0%→real, digest/flagged popping in). -->
      <div v-if="loading || !todayResolved" class="home-loading"><div class="loader"></div></div>

      <!-- Kept mounted via v-show while the loader shows, so TodayView fetches and
           resolves underneath; the whole page reveals at once when it's ready. -->
      <div v-show="!loading && todayResolved">
        <!-- Critical heads-up — rendered ALWAYS (even when Today is live), because a
             data-outage / high-severity finding must caveat everything below it and
             Today's flagged list intentionally excludes 'high'. -->
        <div v-if="criticalFinding" class="home-alert">
          <i class="bi bi-exclamation-triangle-fill"></i>
          <span><strong>{{ criticalFinding.Data && criticalFinding.Data.title }}</strong>
            — {{ criticalFinding.Data && criticalFinding.Data.detail }}</span>
        </div>

        <!-- IA reorg (Today → production): the Today surface. It emits @resolved once
             all its data is in — the boolean says whether it has content, and the
             legacy blocks below are the fallback for stores Today doesn't cover
             (footfall-only, coach-off, no product zone, data outage). -->
        <TodayView @resolved="onTodayResolved" />

        <!-- ===== Legacy home — FALLBACK ONLY, retired once Today is live ===== -->
        <template v-if="todayResolved && !todayActive">
        <!-- ABOVE the divider: signals not tied to a single period (noticed + goals) -->
        <div class="home-top" :class="{ 'home-top--single': !showNoticed }">
          <!-- Allen noticed — a speech balloon, sibling to the digest below -->
          <div v-if="showNoticed" class="noticed-card">
            <div class="noticed-avatar" aria-hidden="true">
              <i class="bi bi-robot"></i>
              <span class="bell-badge"><i class="bi bi-bell-fill"></i></span>
            </div>
            <div class="noticed-bubble">
              <div class="noticed-head">
                <span class="noticed-name">{{ t('today.noticed.name') }}</span>
                <span class="noticed-role">{{ t('today.noticed.role') }}</span>
              </div>
              <ul class="home-findings">
                <li
                  v-for="f in noticedFindings"
                  :key="f.FindingId"
                  :class="['finding', `sev-${(f.Data && f.Data.severity) || 'info'}`]"
                >
                  <div class="finding-body">
                    <span class="finding-title">{{ f.Data && f.Data.title }}</span>
                    <span class="finding-detail">{{ f.Data && f.Data.detail }}</span>
                    <span v-if="f.Data && f.Data.when" class="finding-when">
                      <i class="bi bi-arrow-repeat"></i>{{ f.Data.when }}
                    </span>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          <div class="home-top-right">
            <!-- Active goals — what you're steering toward, with a link to manage them -->
            <div class="panel home-panel goals-card">
              <div class="home-panel-head"><i class="bi bi-flag-fill"></i> {{ t('today.home.goalsHead') }}</div>
              <ul v-if="goals.length" class="home-goals">
                <li v-for="goal in goals" :key="goal.GoalId">
                  <span class="goal-title">{{ goal.GoalData && goal.GoalData.title || t('today.untitledGoal') }}</span>
                  <span v-if="goal.GoalData && goal.GoalData.metric" class="goal-tag">
                    {{ goal.GoalData.metric }}<template v-if="goal.GoalData.zone"> · {{ goal.GoalData.zone }}</template>
                  </span>
                </li>
              </ul>
              <p v-else class="goals-empty">
                {{ t('today.home.goalsEmpty') }}
              </p>
              <router-link to="/settings" class="goals-manage">
                {{ goals.length ? t('today.home.manageGoals') : t('today.home.addGoal') }} <i class="bi bi-arrow-right"></i>
              </router-link>
            </div>

            <!-- Context nudge — only when nothing has been told to Allen yet -->
            <div v-if="!hasContext" class="panel home-panel home-nudge">
              <div class="home-panel-head"><i class="bi bi-lightbulb"></i> {{ t('today.home.nudgeHead') }}</div>
              <p>{{ t('today.home.nudgeBody') }}</p>
              <router-link to="/settings" class="nudge-btn">{{ t('today.home.nudgeBtn') }}</router-link>
            </div>
          </div>
        </div>

        <!-- Divider: labels the SNAPSHOT (cards + digest) and its period. Ask Allen,
             below, isn't bound to the period, so it sits outside this label. -->
        <div class="period-divider">
          <span class="period-label">{{ t('today.home.snapshot') }}</span>
          <span v-if="windowLabel" class="period-pill"><i class="bi bi-calendar3"></i> {{ windowLabel }}</span>
        </div>

        <div v-if="kpis.length" class="home-kpis" :class="{ 'kpis-caveated': criticalFinding }">
          <router-link
            v-for="k in kpis"
            :key="k.label"
            to="/store_level"
            class="kpi-card"
            :style="{ '--accent': k.accent }"
          >
            <div class="kpi-label">{{ k.label }}</div>
            <div class="kpi-val" :style="{ color: toneColor(k.tone) }">{{ k.value }}</div>
            <div class="kpi-foot">{{ k.foot }}</div>
          </router-link>
        </div>
        <div class="home-kpis-foot">
          <router-link to="/store_level" class="home-breakdown-link">
            {{ storeType === 'traffic' ? t('today.home.seeFootfall') : t('today.home.seeBreakdown') }}
            <i class="bi bi-arrow-right"></i>
          </router-link>
        </div>

        <!-- Allen's read of the period / preparing / off -->
        <CoachDigest v-if="coachEnabled && digest" :static-digest="digest" :heading="t('today.home.digestHeading')" />
        <div v-else-if="coachEnabled" class="panel home-slot">
          <div class="allen-avatar small"><i class="bi bi-robot"></i></div>
          <p>{{ t('today.home.preparing') }}</p>
        </div>
        <div v-else class="panel home-slot meet-allen">
          <div class="allen-avatar"><i class="bi bi-robot"></i></div>
          <div>
            <h4>{{ t('today.home.offTitle') }}</h4>
            <p>{{ t('today.home.offBody') }}</p>
            <router-link to="/settings" class="home-cta inline">{{ t('today.home.turnOn') }} <i class="bi bi-arrow-right"></i></router-link>
          </div>
        </div>

        </template>
        <!-- ===== end legacy fallback ===== -->

        <!-- Ask Allen moved to the global docked companion (AllenDock in App.vue) —
             one Allen on every surface, not buried at the bottom of Today (spec §3.5). -->
      </div>
    </Section>
  </div>
</template>

<script setup>
import { ref, computed, onBeforeMount } from 'vue'
import { useI18n } from 'vue-i18n'
import Section from '@/components/ui/Section.vue'
import CoachDigest from './CoachDigest.vue'
import TodayView from './TodayView.vue'
import { useCompanyStore } from '@/stores/CompanyStore'
import { useUserStore } from '@/stores/UserStore'

const companyStore = useCompanyStore()
const userStore = useUserStore()
const { t } = useI18n()
// Allen intro: the first INTRO_VISITS visits on this browser. Storage can be blocked
// (private window) → show it.
const INTRO_VISITS = 3
const showIntro = (() => {
  try {
    const n = Number(localStorage.getItem('stora_home_visits') || 0) + 1
    localStorage.setItem('stora_home_visits', String(n))
    return n <= INTRO_VISITS
  } catch (_) { return true }
})()
const summary = ref(null)
const loading = ref(true)
// TodayView resolves ONCE, after all its data is in: todayResolved flips true and
// todayActive says whether it has content. The page shows a single loader until
// both the home-summary and Today have resolved, so nothing renders piecemeal.
const todayActive = ref(false)
const todayResolved = ref(false)
function onTodayResolved(active) {
  todayActive.value = active
  todayResolved.value = true
}

const companyName = computed(() => companyStore.company?.CompanyName || t('today.yourStore'))
const kpis = computed(() => summary.value?.kpis || [])
const digest = computed(() => (summary.value?.digest && summary.value.digest.digest ? summary.value.digest : null))
const goals = computed(() => summary.value?.goals || [])
const findings = computed(() => companyStore.findings || [])
const storeType = computed(() => summary.value?.store_type || 'measured')
const hasContext = computed(() => !!summary.value?.has_context)
const coachEnabled = computed(() => !!summary.value?.coach_enabled)
const windowLabel = computed(() => {
  const p = summary.value?.period_label
  const h = summary.value?.hours_label
  return p ? (h ? `${p} · ${h}` : p) : ''
})

// Findings → the "what's changing" layer. Severity drives weight: a high finding
// is promoted to the banner (and caveats the cards); the rest list in the balloon.
const SEV_RANK = { high: 0, warning: 1, info: 2, good: 3 }
const sortedFindings = computed(() =>
  [...findings.value].sort((a, b) =>
    (SEV_RANK[a.Data && a.Data.severity] ?? 9) - (SEV_RANK[b.Data && b.Data.severity] ?? 9)))
const criticalFinding = computed(() =>
  findings.value.find((f) => f.Data && f.Data.severity === 'high') || null)
const noticedFindings = computed(() =>
  sortedFindings.value.filter((f) => !(f.Data && f.Data.severity === 'high')))
const showNoticed = computed(() => coachEnabled.value && noticedFindings.value.length > 0)

const TONE = { good: '#2C6F58', watch: '#A56B1F', critical: '#B44A55', muted: '#8fa2b7', neutral: '#1d2b3a' }
function toneColor(t) { return TONE[t] || TONE.neutral }

// Ask Allen — the chat lives in <AskAllenPanel mode="live">. LIVE. (Store-level
// -by-date tools re-enable after the ETL data check.)
const askAllenReady = true
const suggestedQuestions = computed(() => summary.value?.suggested_questions || [])

onBeforeMount(async () => {
  if (!companyStore.company?.CompanyName && userStore.currentCompany?.CompanyId) {
    try { await companyStore.fetchCompanyDetails(userStore.currentCompany.CompanyId) } catch (_) { /* noop */ }
  }
  summary.value = await companyStore.fetchHomeSummary()
  loading.value = false
  if (summary.value?.coach_enabled) {
    try { await companyStore.fetchFindings() } catch (_) { /* noop */ }
  }
})

async function dismissFinding(id) {
  try { await companyStore.dismissFinding(id) } catch (_) { /* noop */ }
}
</script>

<style scoped>
/* Intro — the one place Allen is introduced: a two-tier lead/sub, his avatar
   inline right after his name. */
.home-intro { display: flex; flex-direction: column; gap: 0.14rem; margin: 0 0 1.4rem; }
.intro-lead { margin: 0; font-size: 1.18rem; font-weight: 700; color: #1d2b3a; line-height: 1.3; }
.intro-name { color: var(--stora-accent, #4F7EB3); }
.intro-bot {
  display: inline-flex; align-items: center; justify-content: center;
  width: 24px; height: 24px; margin-left: 0.32rem;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--stora-accent, #4F7EB3), var(--stora-accent-2, #62B5D9));
  color: #fff; font-size: 0.8rem;
  vertical-align: middle; position: relative; top: -0.18em;
  box-shadow: 0 2px 6px rgba(79, 126, 179, 0.3);
}
.intro-sub { margin: 0; font-size: 0.92rem; color: var(--stora-muted, #6b7a86); line-height: 1.5; max-width: 640px; }

.home-loading { display: flex; justify-content: center; padding: 3rem 0; }

/* ===== ABOVE the divider: period-independent signals, grouped on a light tint ===== */
.home-top {
  display: grid;
  grid-template-columns: minmax(0, 1.7fr) minmax(260px, 1fr);
  gap: var(--gap-xl, 24px);
  align-items: start;
  background: #f7faff;
  border: 1px solid #eef2f8;
  border-radius: 14px;
  padding: 1.2rem 1.25rem 1.3rem;
}
.home-top--single { grid-template-columns: 1fr; }
.home-top .panel,
.home-top .noticed-bubble { box-shadow: 0 2px 6px rgba(13, 38, 76, 0.04); }
.home-top-right { display: flex; flex-direction: column; gap: var(--gap, 12px); min-width: 0; }
@media (max-width: 1080px) {
  .home-top { grid-template-columns: 1fr; }
}

/* Allen noticed — a speech balloon, sibling to the digest (avatar + tailed bubble) */
.noticed-card { display: flex; align-items: flex-start; gap: 0.9rem; }
.noticed-avatar {
  position: relative;
  flex: 0 0 auto;
  width: 46px; height: 46px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, var(--stora-accent, #4F7EB3), var(--stora-accent-2, #62B5D9));
  color: #fff; font-size: 1.4rem;
  box-shadow: 0 4px 10px rgba(79, 126, 179, 0.35);
}
.noticed-avatar .bell-badge {
  position: absolute; right: -2px; bottom: -2px;
  width: 20px; height: 20px; border-radius: 50%;
  background: #fff; border: 2px solid #fff;
  box-shadow: 0 0 0 1px var(--stora-border, #e6edf3);
  display: flex; align-items: center; justify-content: center;
  font-size: 11px; color: var(--stora-accent, #4F7EB3);
}
.noticed-bubble {
  position: relative; flex: 1 1 auto;
  background: var(--stora-card, #fff);
  border: 1px solid var(--stora-border, #e6edf3);
  border-radius: 12px;
  padding: 1rem 1.25rem 1.1rem;
}
.noticed-bubble::before {
  content: ''; position: absolute; top: 18px; left: -8px;
  width: 14px; height: 14px;
  background: var(--stora-card, #fff);
  border-left: 1px solid var(--stora-border, #e6edf3);
  border-bottom: 1px solid var(--stora-border, #e6edf3);
  transform: rotate(45deg);
}
.noticed-head { display: flex; align-items: baseline; gap: 0.5rem; margin-bottom: 0.7rem; }
.noticed-name { font-weight: 700; color: var(--stora-accent, #4F7EB3); }
.noticed-role { font-size: 0.8rem; color: var(--stora-muted, #6b7a86); }

.home-findings { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 0.5rem; }
.finding {
  display: flex; align-items: flex-start; gap: 0.4rem;
  border-left: 3px solid #8fa2b7;
  background: #f7f9fc;
  border-radius: 0 8px 8px 0;
  padding: 0.5rem 0.55rem 0.5rem 0.6rem;
}
.finding-body { display: flex; flex-direction: column; gap: 0.1rem; min-width: 0; }
.finding-title { font-weight: 600; font-size: 0.84rem; color: #1d2b3a; line-height: 1.3; }
.finding-detail { font-size: 0.78rem; color: #51637a; line-height: 1.4; }
.finding-dismiss {
  margin-left: auto; background: none; border: none;
  color: #9aa7b5; cursor: pointer; font-size: 0.72rem; padding: 0.1rem 0.15rem; line-height: 1;
}
.finding-dismiss:hover { color: #51637a; }
.finding.sev-info    { border-left-color: #62B5D9; background: #f4f8fc; }
.finding.sev-warning { border-left-color: #D3A741; background: #fff8ec; }
.finding.sev-high    { border-left-color: #C21500; background: #fff3f1; }
.finding.sev-high .finding-title { color: #9a2727; }
.finding.sev-good    { border-left-color: #2C6F58; background: #f1f8f3; }
.finding-when {
  align-self: flex-start; margin-top: 0.3rem;
  font-size: 0.68rem; color: var(--stora-muted, #6b7a86);
  background: #eef2f6; border-radius: 999px; padding: 0.1rem 0.5rem;
  display: inline-flex; align-items: center; gap: 0.28rem;
}
.finding-when i { font-size: 0.62rem; }

/* Cards (goals / nudge) */
.panel {
  background: var(--stora-card, #fff);
  border: 1px solid var(--stora-border, #e6edf3);
  border-radius: 12px;
  box-shadow: 0 6px 14px rgba(13, 38, 76, 0.06);
}
.home-panel { padding: 1rem 1.15rem; }
.home-panel-head {
  display: flex; align-items: center; gap: 0.45rem;
  font-weight: 600; color: #344054; margin-bottom: 0.7rem;
}
.home-panel-head i { color: var(--stora-accent, #4F7EB3); }
.home-goals { list-style: none; padding: 0; margin: 0; }
.home-goals li { display: flex; flex-direction: column; gap: 0.25rem; color: #344054; }
.home-goals li + li { border-top: 1px solid var(--stora-border, #e6edf3); padding-top: 0.55rem; margin-top: 0.55rem; }
.goal-title { font-weight: 500; font-size: 0.9rem; }
.goal-tag {
  align-self: flex-start; font-size: 0.72rem;
  color: var(--stora-accent, #4F7EB3); background: #eef5ff;
  border: 1px solid #dbe7f5; border-radius: 999px; padding: 0.12rem 0.6rem;
}
.goals-empty { margin: 0 0 0.2rem; font-size: 0.85rem; color: var(--stora-muted, #6b7a86); line-height: 1.5; }
.goals-manage {
  display: inline-flex; align-items: center; gap: 0.35rem;
  margin-top: 0.85rem; font-size: 0.82rem; font-weight: 600;
  color: var(--stora-accent, #4F7EB3); text-decoration: none;
}
.goals-manage i { font-size: 0.75rem; transition: transform 0.15s ease; }
.goals-manage:hover i { transform: translateX(3px); }

.home-nudge p { margin: 0 0 0.7rem; color: var(--stora-muted, #6b7a86); font-size: 0.9rem; line-height: 1.5; }
.nudge-btn {
  display: inline-block;
  border: 1px solid var(--stora-accent, #4F7EB3);
  background: #fff; color: var(--stora-accent, #4F7EB3);
  font-weight: 600; font-size: 0.85rem;
  padding: 0.45rem 0.9rem; border-radius: 8px; text-decoration: none;
  transition: background 0.15s ease;
}
.nudge-btn:hover { background: #eef5ff; }

/* ===== The divider: labels the snapshot section + its period ===== */
.period-divider { display: flex; align-items: center; gap: 0.7rem; margin: 1.7rem 0 1.3rem; }
.period-label {
  flex: none; font-size: 0.78rem; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.5px; color: #344054;
}
.period-pill {
  display: inline-flex; align-items: center; gap: 0.4rem; flex: none;
  font-size: 0.82rem; color: var(--stora-muted, #6b7a86);
  background: #f4f7fb; border: 1px solid var(--stora-border, #e6edf3);
  border-radius: 999px; padding: 0.25rem 0.7rem;
}
.period-pill i { color: var(--stora-accent, #4F7EB3); }
.period-divider::after { content: ''; flex: 1 1 auto; height: 1px; background: var(--stora-border, #e6edf3); }

/* ===== BELOW the divider: the snapshot ===== */
.home-alert {
  display: flex; align-items: flex-start; gap: 0.55rem;
  background: #fff3f1; border: 1px solid #f3b3a8; border-left: 4px solid #C21500;
  color: #9a2727; border-radius: 10px;
  padding: 0.7rem 0.95rem; margin: 0 0 1.1rem;
  font-size: 0.9rem; line-height: 1.45;
}
.home-alert i { color: #C21500; font-size: 1rem; margin-top: 0.1rem; }
.home-alert strong { font-weight: 700; }

.home-kpis {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--gap-md, 14px);
  margin: 0 0 0.6rem;
  transition: opacity 0.2s ease;
}
.home-kpis.kpis-caveated { opacity: 0.62; }
@media (max-width: 1080px) { .home-kpis { grid-template-columns: 1fr; } }
.kpi-card {
  display: block; text-decoration: none;
  background: var(--stora-card, #fff);
  border: 1px solid var(--stora-border, #e6edf3);
  border-left: 4px solid var(--accent, #4F7EB3);
  border-radius: 12px; padding: 0.95rem 1.1rem;
  box-shadow: 0 3px 12px rgba(13, 38, 76, 0.05);
  transition: box-shadow 0.15s ease, transform 0.15s ease;
}
.kpi-card:hover { box-shadow: 0 8px 18px rgba(13, 38, 76, 0.1); transform: translateY(-2px); }
.kpi-label { font-size: 0.85rem; color: var(--stora-muted, #6b7a86); margin-bottom: 0.15rem; }
.kpi-val   { font-size: 2rem; font-weight: 800; line-height: 1.15; }
.kpi-foot  { font-size: 0.8rem; color: #9aa7b1; margin-top: 0.1rem; }

.home-kpis-foot { display: flex; justify-content: flex-end; margin: 0 0 1.6rem; }
.home-breakdown-link {
  display: inline-flex; align-items: center; gap: 0.35rem;
  font-size: 0.85rem; font-weight: 600; color: var(--stora-accent, #4F7EB3); text-decoration: none;
}
.home-breakdown-link:hover { text-decoration: underline; }
.home-breakdown-link i { font-size: 0.78rem; transition: transform 0.15s ease; }
.home-breakdown-link:hover i { transform: translateX(3px); }

/* Allen slot placeholders (preparing / off) */
.home-slot { display: flex; align-items: flex-start; gap: 0.9rem; padding: 1.1rem 1.25rem; }
.home-slot p { margin: 0; color: #475467; line-height: 1.55; }
.home-slot h4 { margin: 0 0 0.35rem; font-size: 1.05rem; color: #1d2b3a; }
.allen-avatar {
  flex: 0 0 auto; width: 46px; height: 46px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, var(--stora-accent, #4F7EB3), var(--stora-accent-2, #62B5D9));
  color: #fff; font-size: 1.4rem; box-shadow: 0 4px 10px rgba(79, 126, 179, 0.35);
}
.allen-avatar.small { width: 38px; height: 38px; font-size: 1.15rem; }

.home-cta {
  display: inline-flex; align-items: center; justify-content: center; gap: 0.5rem;
  background: var(--stora-accent, #4F7EB3); color: #fff; font-weight: 600;
  text-decoration: none; padding: 0.7rem 1.2rem; border-radius: 10px;
  box-shadow: 0 6px 14px rgba(13, 38, 76, 0.14);
  transition: background 0.15s ease, transform 0.15s ease;
}
.home-cta.inline { margin-top: 0.6rem; box-shadow: none; padding: 0.5rem 1rem; }
.home-cta:hover { background: #3f6c9e; transform: translateY(-1px); }
.home-cta i { transition: transform 0.15s ease; }
.home-cta:hover i { transform: translateX(3px); }

/* Ask Allen — its own always-available tool, separated from the digest. */
.home-ask {
  margin-top: 1.6rem;
  padding-top: 1.6rem;
  border-top: 1px solid var(--stora-border, #e6edf3);
}
</style>
